import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClientBookingInvoices } from 'service-api/src/hooks/use_client_booking_invoices'
import type { ClientInvoicesByPeriodSearchOptions } from 'service-api/src/types/api'
import { TCustomInvoiceDto } from 'service-api/src/types/general'
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_SORT_ORDER, SortOrder } from './use_bookings_url_state'

export type BookingInvoiceView = 'list' | 'calendar'
export type BookingInvoiceFilters = Pick<ClientInvoicesByPeriodSearchOptions, 'invoiceSubStatus'> & {
  'locationServiceType.serviceTypeId'?: string
}
type BookingRequestOptions = Omit<ClientInvoicesByPeriodSearchOptions, 'locationServiceType.serviceTypeId'> &
  Pick<BookingInvoiceFilters, 'locationServiceType.serviceTypeId'>
export interface BookingInvoiceRange {
  start: string
  end: string
}

export interface UseBookingInvoicesOptions {
  view: BookingInvoiceView
  filters?: BookingInvoiceFilters
  range?: BookingInvoiceRange
  sortOrder?: SortOrder
  limit?: number
}

const NO_FILTERS: BookingInvoiceFilters = {}

const INCLUDES = ['order', 'requestPeriods', 'items.petRelations.invoicePet.locationPetProfile']

const getSearchOptions = (options: BookingRequestOptions): ClientInvoicesByPeriodSearchOptions => {
  const { 'locationServiceType.serviceTypeId': serviceTypeId, ...rest } = options
  return {
    ...rest,
    includes: INCLUDES,
    'locationServiceType.serviceTypeId': serviceTypeId ? [serviceTypeId] : undefined,
  }
}

/**
 * Accumulates invoices for a view.
 * In list view, filter or sort changes start a fresh list.
 * In calendar view, date range changes merge results into the existing list deduped by id.
 */
export const useBookingInvoices = (
  accountName: string,
  {
    view,
    filters = NO_FILTERS,
    range,
    sortOrder = DEFAULT_SORT_ORDER,
    limit = DEFAULT_ROWS_PER_PAGE,
  }: UseBookingInvoicesOptions
) => {
  const [pager, setPager] = useState<{ scope?: string; nextToken?: string; requested?: string }>({})
  const [records, setRecords] = useState<{ key?: string; byId: Map<string, TCustomInvoiceDto> }>({ byId: new Map() })

  const activeRange = view === 'calendar' ? range : undefined

  // Cursor/pagination helpers
  const listKey = useMemo(() => JSON.stringify({ view, filters, sortOrder }), [view, filters, sortOrder])
  const scope = useMemo(() => JSON.stringify({ listKey, range: activeRange }), [listKey, activeRange])
  const requested = pager.scope === scope ? pager.requested : undefined

  const requestOptions: BookingRequestOptions = useMemo(
    () => ({
      sortOrder,
      ...filters,
      ...(activeRange ? { startDate: `gte_${activeRange.start}`, endDate: `lte_${activeRange.end}` } : {}),
      limit,
      nextToken: requested,
    }),
    [sortOrder, limit, filters, requested, activeRange]
  )

  const searchOptions = useMemo(() => getSearchOptions(requestOptions), [requestOptions])
  const fetchKey = useMemo(() => JSON.stringify([listKey, requestOptions]), [listKey, requestOptions])

  // The calendar has no date range until FullCalendar reports its grid, so hold the request until then.
  const enabled = view === 'list' || !!activeRange
  const { data, isLoading } = useClientBookingInvoices(accountName, searchOptions, { enabled })

  // react-query returns a new data object on every render, so each fetch's result is merged once.
  const mergedKeyRef = useRef<string>()
  useEffect(() => {
    if (!data?.results || mergedKeyRef.current === fetchKey) return
    mergedKeyRef.current = fetchKey
    const nextToken = data.nextToken ?? undefined

    setRecords((prev) => {
      const byId = new Map(prev.key === listKey ? prev.byId : undefined)
      data.results.forEach((invoice) => {
        if (invoice.id) byId.set(invoice.id, invoice)
      })
      return { key: listKey, byId }
    })

    // Calendar view follows nextToken until it is undefined,
    // list view waits for loadMore() to be called manually
    setPager({
      scope,
      nextToken,
      requested: view === 'calendar' && nextToken ? nextToken : requested,
    })
  }, [data, fetchKey, listKey, scope, view, requested])

  const invoices = useMemo(() => (records.key === listKey ? Array.from(records.byId.values()) : []), [records, listKey])

  const hasMore = pager.scope === scope && !!pager.nextToken

  const loadMore = useCallback(() => {
    setPager((prev) => (prev.scope === scope && prev.nextToken ? { ...prev, requested: prev.nextToken } : prev))
  }, [scope])

  return useMemo(() => ({ invoices, isLoading, hasMore, loadMore }), [invoices, isLoading, hasMore, loadMore])
}
